import React from 'react'
import { createPortal } from 'react-dom'
import { swalSuccess } from '../../../../functions/swalOptions'
import { getNameUser } from '../../../../functions/agent/agentModuleComponent/getNameUser'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'
import { dubaiInput, dubaiUTC } from '../../keyViewings/keyHandover'
import { handoverUserName, PendingKeyOperations, usePendingKeyOperations } from '../../keyViewings/KeyTransferPanel'
import { type User } from '../../../../store/slices/userSlice'
import { type BsoUnitAnalyticsRow } from '../adminBsoUnitStatistics'
import { DEFAULT_MANUAL_VIEWING_STATUS, isManualActionUserConfigured, manualActionUserConfig, MANUAL_VIEWING_STATUS_OPTIONS } from './manualOperations.config'
import { createManualViewing } from './manualViewingActions'
import { ManualUserSelector } from './ManualUserSelector'
import { type ManualViewingStatus, type ManualViewingUserMode } from './manualOperations.types'
import './ManualViewingModal.scss'

const getDefaultDatetimeLocal = (): string => {
    const date = new Date()
    date.setSeconds(0, 0)
    if (date.getMinutes() <= 30) {
        date.setMinutes(30)
    } else {
        date.setHours(date.getHours() + 1, 0)
    }
    return dubaiInput(date)
}

export const ManualViewingModal = ({
    unit,
    users,
    onClose
}: {
    unit: BsoUnitAnalyticsRow | null
    users: User[]
    onClose: () => void
}): JSX.Element | null => {
    const dispatch = useAppDispatch()
    const pending = usePendingKeyOperations()
    const admin = useAppSelector((state) => state.user.currentUser) as User
    const [received, setReceived] = React.useState(false)
    const [pickup, setPickup] = React.useState(() => dubaiInput())
    const [returnAt, setReturnAt] = React.useState(() => dubaiInput(new Date(Date.now() + 3600000)))
    const [userMode, setUserMode] = React.useState<ManualViewingUserMode>('existing-user')
    const [selectedUser, setSelectedUser] = React.useState<User | null>(null)
    const [datetimeLocal, setDatetimeLocal] = React.useState(getDefaultDatetimeLocal)
    const [status, setStatus] = React.useState<ManualViewingStatus>(DEFAULT_MANUAL_VIEWING_STATUS)
    const [visitorName, setVisitorName] = React.useState('')
    const [complaint, setComplaint] = React.useState('')
    const [digitalLockPass, setDigitalLockPass] = React.useState('')
    const [saving, setSaving] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)

    React.useEffect(() => {
        if (unit == null) return
        setUserMode('existing-user')
        setReceived(false)
        setPickup(dubaiInput())
        setReturnAt(dubaiInput(new Date(Date.now() + 3600000)))
        setSelectedUser(null)
        setDatetimeLocal(getDefaultDatetimeLocal())
        setStatus(DEFAULT_MANUAL_VIEWING_STATUS)
        setVisitorName('')
        setComplaint('')
        setDigitalLockPass('')
        setSaving(false)
        setError(null)
    }, [unit])

    React.useEffect(() => {
        if (unit == null) return
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && !saving) onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [unit, saving, onClose])

    if (unit == null || typeof document === 'undefined') return null

    const manualUserIsConfigured = isManualActionUserConfigured()
    const hasDigitalLock = unit.accessStatuses.includes('Digital lock')
    const accessStatuses = [
        ...(unit.conditionStatus != null ? [unit.conditionStatus] : []),
        ...unit.accessStatuses
    ]
    const userId = userMode === 'manual-bso' ? manualActionUserConfig.id : selectedUser?.id
    const manualConfigError = userMode === 'manual-bso' && !manualUserIsConfigured
        ? 'Manual by BSO user is not configured. Add REACT_APP_BSO_MANUAL_ACTION_USER_ID.'
        : null
    const validationError =
        datetimeLocal.trim() === ''
            ? 'Viewing date/time is required.'
            : status.trim() === ''
                ? 'Viewing status is required.'
                : userMode === 'existing-user' && selectedUser == null
                    ? 'Select an existing user.'
                    : userMode === 'manual-bso' && !manualUserIsConfigured
                        ? 'Manual by BSO user is not configured.'
                        : null

    const submitDisabled = saving || pending.some((operation) => operation.kind === 'received' && operation.unitId === unit.unitId) || validationError != null || userId == null || (received && (userMode !== 'existing-user' || selectedUser == null || !pickup || !returnAt || returnAt <= pickup))

    const handleSubmit = async (): Promise<void> => {
        if (submitDisabled || userId == null) {
            setError(validationError)
            return
        }

        setSaving(true)
        setError(null)
        let receivedKey
        try {
            if (received && selectedUser != null) receivedKey = { holder: { id: selectedUser.id, name: handoverUserName(selectedUser) }, admin: { id: admin.id, name: handoverUserName(admin) }, pickup: dubaiUTC(pickup), returnAt: dubaiUTC(returnAt) }
        } catch (e) { setSaving(false); setError(e instanceof Error ? e.message : 'Invalid receipt dates.'); return }
        const result = await dispatch(createManualViewing({
            receivedKey,
            unit,
            userId,
            recipientName: selectedUser != null ? getNameUser(selectedUser).trim() : manualActionUserConfig.email,
            datetimeLocal,
            status,
            visitorName,
            complaint,
            digitalLockPass: hasDigitalLock ? digitalLockPass : undefined
        }))

        if (result.isSuccessful) {
            setSaving(false)
            onClose()
            void swalSuccess('Viewing has been created.', undefined, 'Success')
            return
        }

        setSaving(false)
        setError(result.error ?? 'Could not create viewing.')
    }

    return createPortal(
        <div className="manual-viewing__backdrop" onClick={saving ? undefined : onClose}>
            <div className="manual-viewing__modal" role="dialog" aria-modal="true" aria-labelledby="manual-viewing-title" onClick={(event) => event.stopPropagation()}>
                <header className="manual-viewing__header">
                    <div>
                        <h2 id="manual-viewing-title">Add Viewing</h2>
                        <p>Create a normal viewing for the selected unit.</p>
                    </div>
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} disabled={saving}>Close</button>
                </header>

                <div className="manual-viewing__body">
                    <PendingKeyOperations onRefresh={async () => {}} />
                    <section className="manual-viewing__unit">
                        <img src={unit.imageUrl} alt={unit.headline} />
                        <div>
                            <strong>{unit.headline}</strong>
                            <span>{unit.propertyName}</span>
                            <small>{unit.areaName}{unit.unitNumber != null ? `, Unit ${unit.unitNumber}` : `, Ref ${unit.unitReference}`}</small>
                            <div className="manual-viewing__access-list" aria-label="Unit access information">
                                {accessStatuses.length > 0
                                    ? accessStatuses.map((accessStatus) => <span className="badge badge-light" key={accessStatus}>{accessStatus}</span>)
                                    : <span className="badge badge-light">No access status</span>}
                            </div>
                        </div>
                    </section>

                    <section className="manual-viewing__section">
                        <label className="form-label">User source</label>
                        <div className="manual-viewing__mode-toggle" role="group" aria-label="User source">
                            <button className={userMode === 'existing-user' ? 'active' : ''} type="button" disabled={saving} onClick={() => setUserMode('existing-user')}>
                                Existing User
                            </button>
                            <button className={userMode === 'manual-bso' ? 'active' : ''} type="button" disabled={saving || received} onClick={() => setUserMode('manual-bso')}>
                                Manual by BSO
                            </button>
                        </div>
                    </section>

                    {userMode === 'existing-user'
                        ? (
                            <ManualUserSelector
                                users={users}
                                selectedUserId={selectedUser?.id ?? null}
                                disabled={saving}
                                onSelect={(user) => {
                                    setSelectedUser(user)
                                    setError(null)
                                }}
                            />
                        )
                        : (
                            <section className={`manual-viewing__manual-user${manualUserIsConfigured ? '' : ' manual-viewing__manual-user--error'}`}>
                                <strong>Manual by BSO</strong>
                                <span>Use {manualActionUserConfig.email || 'configured system account'} system account for external/manual operations.</span>
                                {manualUserIsConfigured
                                    ? <small>User ID {String(manualActionUserConfig.id)}</small>
                                    : <small>Missing REACT_APP_BSO_MANUAL_ACTION_USER_ID.</small>}
                            </section>
                        )}

                    <section className="manual-viewing__form-grid">
                        <div>
                            <label className="form-label" htmlFor="manual-viewing-datetime">Viewing date/time</label>
                            <input
                                id="manual-viewing-datetime"
                                className="form-control form-control-sm"
                                type="datetime-local"
                                step={1800}
                                value={datetimeLocal}
                                disabled={saving}
                                onChange={(event) => {
                                    setDatetimeLocal(event.target.value)
                                    setError(null)
                                }}
                            />
                        </div>
                        <div>
                            <label className="form-label" htmlFor="manual-viewing-status">Status</label>
                            <select
                                id="manual-viewing-status"
                                className="form-select form-select-sm"
                                value={received ? 'Approved' : status}
                                disabled={saving || received}
                                onChange={(event) => {
                                    setStatus(event.target.value as ManualViewingStatus)
                                    setError(null)
                                }}
                            >
                                {MANUAL_VIEWING_STATUS_OPTIONS.map((option) => (
                                    <option value={option} key={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                    </section>

                    <section className="manual-viewing__form-grid">
                        <div>
                            <label className="form-label" htmlFor="manual-viewing-visitor-name">Visitor Name</label>
                            <input
                                id="manual-viewing-visitor-name"
                                className="form-control form-control-sm"
                                type="text"
                                value={visitorName}
                                disabled={saving}
                                placeholder="Optional"
                                onChange={(event) => setVisitorName(event.target.value)}
                            />
                        </div>
                        {hasDigitalLock && (
                            <div>
                                <label className="form-label" htmlFor="manual-viewing-digital-lock-pass">Digital Lock Pass</label>
                                <input
                                    id="manual-viewing-digital-lock-pass"
                                    className="form-control form-control-sm"
                                    type="text"
                                    value={digitalLockPass}
                                    disabled={saving}
                                    placeholder="Optional"
                                    onChange={(event) => setDigitalLockPass(event.target.value)}
                                />
                            </div>
                        )}
                    </section>

                    {unit.accessStatuses.includes('Keys in BSO office') && <section>
                        <label><input type="checkbox" checked={received} disabled={saving} onChange={(event) => { setReceived(event.target.checked); if (event.target.checked) setUserMode('existing-user') }} /> Key already received</label>
                        {received && <div className="manual-viewing__form-grid">
                            <p>The selected existing user is the actual holder. Viewing will be Approved; the key will be with this user, awaiting return. Times are Dubai time.</p>
                            <label>Received at<input className="form-control" type="datetime-local" value={pickup} disabled={saving} onChange={(event) => setPickup(event.target.value)} /></label>
                            <label>Return due<input className="form-control" type="datetime-local" value={returnAt} disabled={saving} onChange={(event) => setReturnAt(event.target.value)} /></label>
                        </div>}
                    </section>}
                    <section>
                        <label className="form-label" htmlFor="manual-viewing-complaint">Complaint</label>
                        <textarea
                            id="manual-viewing-complaint"
                            className="form-control form-control-sm"
                            value={complaint}
                            rows={3}
                            disabled={saving}
                            placeholder="Optional internal notes"
                            onChange={(event) => setComplaint(event.target.value)}
                        />
                    </section>

                    {(error != null || manualConfigError != null) && (
                        <div className="alert alert-light-danger mb-0">{error ?? manualConfigError}</div>
                    )}
                </div>

                <footer className="manual-viewing__footer">
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="btn btn-sm btn-primary" type="button" onClick={() => { void handleSubmit() }} disabled={submitDisabled}>
                        {saving ? 'Creating...' : 'Create Viewing'}
                    </button>
                </footer>
            </div>
        </div>,
        document.body
    )
}
